import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CctvCard — plays a canned CCTV-style video with YOLO-derived bounding
 * boxes drawn as an SVG overlay in perfect sync with playback time.
 *
 * Honesty: both surfaces are SIMULATED. Stop-cam counts waiting-crowd
 * (demand → λ_downstream). In-bus cam counts onboard riders (occupancy
 * → C_residual). Two DISTINCT cards — never merge them. See SHARPER §7.
 *
 * Bbox JSON schema (produced by scripts/yolo_cctv.py):
 *   { fps, width, height, frames: [{ t, boxes:[[x1,y1,x2,y2,conf]], count }] }
 *
 * If the JSON is missing (asset agent didn't land it), the card still
 * mounts and shows a plausible static count so the demo never crashes.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MockPill } from "../common/MockPill";
import { getBaseUrl } from "../../lib/api";
const cardStyle = {
    padding: "16px 20px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
const videoWrapStyle = {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000d10",
    borderRadius: 6,
    overflow: "hidden",
    border: "1px solid var(--color-pebble)",
};
const overlayStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
};
const countChipStyle = {
    position: "absolute",
    top: 8,
    left: 8,
    padding: "6px 10px",
    background: "rgba(0, 13, 16, 0.72)",
    color: "#e8f6f0",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    display: "flex",
    gap: 8,
    alignItems: "baseline",
};
const timestampStyle = {
    position: "absolute",
    top: 8,
    right: 8,
    padding: "4px 8px",
    background: "rgba(0, 13, 16, 0.72)",
    color: "#e8f6f0",
    borderRadius: 4,
    fontSize: 10,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    letterSpacing: "0.02em",
};
const bodyRowStyle = {
    display: "flex",
    gap: 12,
    alignItems: "stretch",
};
const metricBoxStyle = {
    flex: 1,
    padding: "10px 12px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 2,
};
const metricLabelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
};
const wiringStyle = {
    fontSize: 12,
    color: "var(--color-cool-ash)",
    lineHeight: 1.5,
    paddingTop: 4,
    borderTop: "1px dashed var(--color-pebble)",
};
const KIND_LABEL = {
    stop: "Waiting",
    bus: "Onboard",
};
function fmtTs(t) {
    const mm = Math.floor(t / 60);
    const ss = Math.floor(t % 60);
    return `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
}
function findFrame(doc, t) {
    const frames = doc.frames;
    if (frames.length === 0)
        return null;
    let lo = 0;
    let hi = frames.length - 1;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if ((frames[mid]?.t ?? 0) <= t)
            lo = mid;
        else
            hi = mid - 1;
    }
    return frames[lo] ?? null;
}
export function CctvCard({ kind, videoSrc, bboxSrc, signalLabel, wiring, fallbackCount, targetLabel, }) {
    const videoRef = useRef(null);
    const [doc, setDoc] = useState(null);
    const [docError, setDocError] = useState(null);
    const [time, setTime] = useState(0);
    useEffect(() => {
        let cancelled = false;
        fetch(bboxSrc)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then((data) => {
            if (!cancelled)
                setDoc(data);
        })
            .catch((err) => {
            if (!cancelled)
                setDocError(String(err.message ?? err));
        });
        return () => {
            cancelled = true;
        };
    }, [bboxSrc]);
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const onTime = () => setTime(video.currentTime);
        video.addEventListener("timeupdate", onTime);
        return () => video.removeEventListener("timeupdate", onTime);
    }, []);
    const activeFrame = useMemo(() => (doc ? findFrame(doc, time) : null), [doc, time]);
    const count = activeFrame?.count ?? fallbackCount;
    // Phase 12: stop-cam pushes waiting count to the backend signal registry
    // (in-bus cam does the same from BusCctvModal). Copilot cites the same
    // number the judge sees on-screen. Throttled to once/sec per surface.
    const lastPostedRef = useRef({
        t: 0,
        count: -1,
    });
    useEffect(() => {
        if (!doc)
            return;
        const now = Date.now();
        if (now - lastPostedRef.current.t < 1000 &&
            count === lastPostedRef.current.count) {
            return;
        }
        lastPostedRef.current = { t: now, count };
        const path = kind === "stop" ? "/api/cctv/waiting" : "/api/cctv/occupancy";
        const body = kind === "stop"
            ? { stop_id: targetLabel.split(" ")[0], waiting: count, frame_time_s: time }
            : {
                vehicle_id: targetLabel.split(" ")[0],
                onboard: count,
                capacity: 40,
                frame_time_s: time,
            };
        void fetch(`${getBaseUrl()}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).catch(() => {
            /* best-effort */
        });
    }, [count, time, doc, kind, targetLabel]);
    const viewBox = doc ? `0 0 ${doc.width} ${doc.height}` : "0 0 640 360";
    const strokeColor = kind === "stop" ? "#bc7155" : "#4fa3c7";
    return (_jsxs("div", { style: cardStyle, "data-testid": `cctv-card-${kind}`, children: [_jsxs("div", { style: videoWrapStyle, children: [_jsx("video", { ref: videoRef, src: videoSrc, autoPlay: true, muted: true, loop: true, playsInline: true, preload: "auto", style: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }, onError: () => setDocError((e) => e ?? "video failed to load") }), _jsx("svg", { style: overlayStyle, viewBox: viewBox, preserveAspectRatio: "none", "aria-hidden": true, children: (activeFrame?.boxes ?? []).map((b, i) => {
                            const [x1, y1, x2, y2] = b;
                            return (_jsx("rect", { x: x1, y: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1), fill: "none", stroke: strokeColor, strokeWidth: 2, vectorEffect: "non-scaling-stroke" }, i));
                        }) }), _jsxs("div", { style: countChipStyle, children: [_jsx("span", { style: { fontSize: 20, lineHeight: 1 }, children: count }), _jsx("span", { style: {
                                    fontSize: 10,
                                    opacity: 0.8,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                }, children: KIND_LABEL[kind] })] }), _jsx("div", { style: timestampStyle, children: `CAM · ${fmtTs(time)}` })] }), _jsxs("div", { style: bodyRowStyle, children: [_jsxs("div", { style: metricBoxStyle, children: [_jsx("div", { style: metricLabelStyle, children: "Signal" }), _jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: signalLabel })] }), _jsxs("div", { style: metricBoxStyle, children: [_jsx("div", { style: metricLabelStyle, children: "Target" }), _jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: targetLabel })] })] }), _jsxs("div", { style: wiringStyle, children: [wiring, docError ? (_jsxs("div", { style: { marginTop: 6, color: "#a25c46" }, children: [_jsx(MockPill, { kind: "simulated", label: "FALLBACK COUNT" }), " ", _jsx("span", { style: { fontSize: 11 }, children: "bbox stream unavailable \u2014 showing static count. Re-run scripts/yolo_cctv.py." })] })) : null] })] }));
}
